import React from 'react';
import './style.scss';
import { isRTL } from '../../../services/userModel.js';
import classNames from 'classnames';
import { getTranslateTitle } from '../../../utils/objects-util.js';
import EditableTable from '../EditableTable/EditableTable.jsx';
import { connect } from 'react-redux';
import preloader from "../../../assets/images/preloader.GIF";
import { CONTROL_TYPE, MENU_ITEM_TYPE } from '../../../utils/enums.js';
import { getOrderSource } from '../../../utils/order.js';

// One row per menu item (product, option or suboption): the item's own SKU and price, then
// one SKU override column per order source. Per-source PRICES are deliberately absent —
// they belong to the per-source price screen.
// Options have no price at all, so their price cell renders empty and read-only.
const hasPrice = item => item?.type !== MENU_ITEM_TYPE.option;

const isEmptyValue = value => value === null || value === undefined || String(value).trim() === '';

// Prices compare as numbers (the input hands back "10" for a loaded 10, and "10.0" is still
// 10); SKUs compare as text, because "007" and "7" are different codes.
const isSameValue = (accessor, current, original) => {
  if (isEmptyValue(current) || isEmptyValue(original)) {
    return isEmptyValue(current) && isEmptyValue(original);
  }

  if (accessor === 'price') {
    return Number(current) === Number(original);
  }

  return String(current).trim() === String(original).trim();
};

// EditableTable marks a cell `<accessor>-isDirty` on every edit and never unmarks it, so an
// edit typed back to the loaded value would still count as a change. Settle each row against
// its originalValues instead: a cell that matches again gets its loaded value back — the
// exact value, so the row compares equal to the unedited one — and loses the mark. The mark
// then means "really changed", and the highlight, the Save button and the save payload all
// read that same answer.
const settleRow = row => {
  const originals = row?.originalValues;

  if (!originals) {
    return row;
  }

  let settled = row;

  Object.keys(originals).forEach(accessor => {
    const dirtyKey = `${accessor}-isDirty`;

    if (settled.hasOwnProperty(dirtyKey) && isSameValue(accessor, settled[accessor], originals[accessor])) {
      if (settled === row) {
        settled = { ...row };
      }

      settled[accessor] = originals[accessor];
      delete settled[dirtyKey];
    }
  });

  return settled;
};

const SectionItemMenuData = ({ t, item, column, onPropChange, isLoading, translationsLanguage }) => {
  const handleOnPropChange = (propValue, ...rest) => {
    const settled = Array.isArray(propValue?.values)
      ? { ...propValue, values: propValue.values.map(settleRow) }
      : propValue;

    onPropChange(settled, ...rest);
  };

  const providers = item?.providers || [];
  // name + base sku + base price + one sku column per source
  const columnsCount = 3 + providers.length;
  const columnsWidth = 100 / columnsCount;

  const newColumns = column.columns.map(c => ({...c, width: `${columnsWidth}%`, fixedWidth: `${columnsWidth}%`})).concat([
    {
      title: 'settings.menu_data.SKU_LABEL',
      accessor: 'sku',
      width: `${columnsWidth}%`,
      fixedWidth: `${columnsWidth}%`,
      markWhenDirty: true,
      type: CONTROL_TYPE.inputText,
    },
    {
      title: 'settings.menu_data.PRICE_LABEL',
      accessor: 'price',
      width: `${columnsWidth}%`,
      fixedWidth: `${columnsWidth}%`,
      markWhenDirty: true,
      isShow: hasPrice,
      type: CONTROL_TYPE.inputNumber,
    },
  ]).concat(providers.map(source => ({
    title: `${getTranslateTitle('settings.menu_data.SKU_LABEL')} ${getOrderSource(source)}`,
    accessor: `sku-${source}`,
    width: `${columnsWidth}%`,
    fixedWidth: `${columnsWidth}%`,
    markWhenDirty: true,
    type: CONTROL_TYPE.inputText,
  }))).filter(Boolean);

  return (
    <div className={classNames('sectionItemMenuData', { rtl: isRTL() })}>
      <table className={classNames('header')}>
        <tbody>
          <tr>
            {newColumns?.map((column, index) => {
              return (
                <td
                  key={`headerItem-${column.title}-${index}`}
                  className={classNames('headerItem', {
                    centered: column.centered,
                  })}
                  style={{
                    width: column.width ? `${column.width}` : `auto`,
                    textAlign: column.textRight ? 'right' : '',
                  }}
                >
                  {getTranslateTitle(column.title)}
                  &nbsp;
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      {isLoading && (
        <div className={classNames('loaderWrapper')}>
          <img className='preloader' src={preloader} alt='preloader' />
        </div>
      )}
      <EditableTable virtualized model={{...column, columns: newColumns}} items={item?.values} isTranslationRtl={translationsLanguage === "he" || translationsLanguage === "ar"} isLoading={false} onPropChange={handleOnPropChange} />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    isLoading: state.sectionsWrapperData.isLoading,
    translationsLanguage: state.restaurant?.data?.translations?.language
  };
};

export default connect(mapStateToProps)(SectionItemMenuData);
